import React from 'react';
import { Redirect } from 'react-router-dom';

import { PageCodeComparisonType } from 'constants/claims';
import { PAGE_CODES } from 'constants/pageCodes';
import { lazyWithRetry } from 'utils/chunkLoadRecovery';
import { EnhancedPath } from 'utils/history';

export const MARGIN_CENTER_ROUTING_PARAMS = {
  priceSourceId: 'priceSourceId',
  section: 'section',
  uploadId: 'uploadId',
  executionId: 'executionId',
  tab: 'tab',
};

export const RATE_SHEET_SOURCE_SECTION = {
  UPLOADS: 'uploads',
};

export const POLICY_TAB = {
  OVERVIEW: 'overview',
  MARGINS: 'margins',
  ADJUSTMENTS: 'adjustments',
  PASS_THROUGH: 'pass-through',
  GUARDRAILS: 'guardrails',
};

export const PRODUCTS_TAB = {
  PROGRAMS: 'programs',
  CHANNELS: 'channels',
  CHANNEL_MAPPING: 'channel-mapping',
  INDEX_TYPES: 'index-types',
};

export const REPORTS_TAB = {
  MARGIN: 'margin',
  FAIR_LENDING: 'fair-lending',
  QUOTE_REPLAY: 'quote-replay',
};

const TAB_SEGMENT = `:${MARGIN_CENTER_ROUTING_PARAMS.tab}?`;

// LEN-804: the 13-item rail became six items. Each item's base path is what the rail links to; the
// `*Tab` paths carry the in-page tab so a tab survives a refresh and can be linked to.
export class MarginCenterPaths {
  static root = new EnhancedPath('/margin-center');
  static today = new EnhancedPath('/margin-center/today');
  static policy = new EnhancedPath('/margin-center/policy');
  static policyTab = new EnhancedPath(`/margin-center/policy/${TAB_SEGMENT}`);
  static approvals = new EnhancedPath('/margin-center/approvals');
  static sources = new EnhancedPath('/margin-center/sources');
  // The optional segments deep-link the upload wizard (uploads/:uploadId) and the AI run open in it
  // (:executionId), so a labelling run's detail drawer survives a refresh and can be linked to.
  static rateSheetSource = new EnhancedPath(
    `/margin-center/sources/:${MARGIN_CENTER_ROUTING_PARAMS.priceSourceId}/:${MARGIN_CENTER_ROUTING_PARAMS.section}?/:${MARGIN_CENTER_ROUTING_PARAMS.uploadId}?/:${MARGIN_CENTER_ROUTING_PARAMS.executionId}?`,
  );
  static products = new EnhancedPath('/margin-center/products');
  static productsTab = new EnhancedPath(`/margin-center/products/${TAB_SEGMENT}`);
  static reports = new EnhancedPath('/margin-center/reports');
  static reportsTab = new EnhancedPath(`/margin-center/reports/${TAB_SEGMENT}`);
}

// Pre-LEN-804 URLs. Kept only as redirect sources so bookmarks and deep links keep working.
const LEGACY_PATHS = {
  cockpit: '/margin-center/cockpit',
  console: '/margin-center/console',
  rules: '/margin-center/rules',
  capture: '/margin-center/capture',
  guardrails: '/margin-center/guardrails',
  guardrailsTab: '/margin-center/guardrails/:tab',
  preview: '/margin-center/preview',
  concessions: '/margin-center/concessions',
  settings: '/margin-center/settings',
  rateSheetSource: `/margin-center/settings/rate-sheet-sources/:${MARGIN_CENTER_ROUTING_PARAMS.priceSourceId}/:${MARGIN_CENTER_ROUTING_PARAMS.section}?/:${MARGIN_CENTER_ROUTING_PARAMS.uploadId}?/:${MARGIN_CENTER_ROUTING_PARAMS.executionId}?`,
  programs: '/margin-center/programs',
  programsMapper: '/margin-center/programs/mapper',
  programIndexTypes: '/margin-center/programs/index-types',
  channels: '/margin-center/channels',
  dashboards: '/margin-center/dashboards',
  surveillance: '/margin-center/surveillance',
  audit: '/margin-center/audit',
};

// The Margin Center's top-level entry + its read/landing surfaces (Today, Reports) open for a holder
// of ANY margin/programs/channels claim — the individual authoring tabs enforce their own specific
// claim (marginCriteria / marginGuardrail / marginChangeRequest). Gating on this instead of
// adjustmentCriteria (the legacy LLPA surface, which is company-restricted) is DEV-6325.
export const MARGIN_CENTER_ANY_CLAIM = {
  comparisonType: PageCodeComparisonType.OR,
  pageCodes: [
    PAGE_CODES.MARGIN_CRITERIA,
    PAGE_CODES.MARGIN_GUARDRAIL,
    PAGE_CODES.MARGIN_CHANGE_REQUEST,
    PAGE_CODES.PROGRAM,
    PAGE_CODES.CHANNEL,
  ],
};

const PRICING_POLICY_CLAIM = {
  comparisonType: PageCodeComparisonType.OR,
  pageCodes: [PAGE_CODES.MARGIN_CRITERIA, PAGE_CODES.MARGIN_GUARDRAIL],
};

const PRODUCTS_CLAIM = {
  comparisonType: PageCodeComparisonType.OR,
  pageCodes: [
    PAGE_CODES.PROGRAM,
    PAGE_CODES.CHANNEL,
    PAGE_CODES.CHANNEL_PROGRAM,
    PAGE_CODES.INTERNAL_INDEX_TYPE,
  ],
};

const redirectTo = to => () => <Redirect to={to} />;

const redirectRoute = (path, to, pageCode = MARGIN_CENTER_ANY_CLAIM) => ({
  path,
  component: redirectTo(to),
  pageCode,
  exact: true,
});

const routesConfig = {
  settings: {
    layout: {},
  },
  routes: [
    {
      path: MarginCenterPaths.today.path,
      component: lazyWithRetry(() => import('app/pages/marginCenter/TodayPage')),
      pageCode: MARGIN_CENTER_ANY_CLAIM,
      exact: true,
    },
    {
      path: MarginCenterPaths.policyTab.path,
      component: lazyWithRetry(() => import('app/pages/marginCenter/PolicyPage')),
      pageCode: PRICING_POLICY_CLAIM,
      exact: true,
    },
    {
      path: MarginCenterPaths.approvals.path,
      component: lazyWithRetry(() => import('app/pages/marginCenter/ApprovalsPage')),
      pageCode: PAGE_CODES.MARGIN_CHANGE_REQUEST,
      exact: true,
    },
    {
      path: MarginCenterPaths.sources.path,
      component: lazyWithRetry(() => import('app/pages/marginCenter/SettingsPage')),
      pageCode: PAGE_CODES.MARGIN_GUARDRAIL,
      exact: true,
    },
    {
      path: MarginCenterPaths.rateSheetSource.path,
      component: lazyWithRetry(() => import('app/pages/marginCenter/RateSheetSourcePage')),
      pageCode: PAGE_CODES.MARGIN_GUARDRAIL,
      exact: true,
    },
    {
      path: MarginCenterPaths.productsTab.path,
      component: lazyWithRetry(() => import('app/pages/marginCenter/ProductsPage')),
      pageCode: PRODUCTS_CLAIM,
      exact: true,
    },
    {
      path: MarginCenterPaths.reportsTab.path,
      component: lazyWithRetry(() => import('app/pages/marginCenter/ReportsPage')),
      pageCode: MARGIN_CENTER_ANY_CLAIM,
      exact: true,
    },
    redirectRoute(MarginCenterPaths.root.path, MarginCenterPaths.today.path),
    redirectRoute(LEGACY_PATHS.cockpit, MarginCenterPaths.today.path),
    redirectRoute(
      LEGACY_PATHS.console,
      MarginCenterPaths.policyTab.generatePath({ tab: POLICY_TAB.MARGINS }),
      PAGE_CODES.MARGIN_CRITERIA,
    ),
    redirectRoute(
      LEGACY_PATHS.rules,
      MarginCenterPaths.policyTab.generatePath({ tab: POLICY_TAB.ADJUSTMENTS }),
      PAGE_CODES.MARGIN_CRITERIA,
    ),
    redirectRoute(
      LEGACY_PATHS.capture,
      MarginCenterPaths.policyTab.generatePath({ tab: POLICY_TAB.PASS_THROUGH }),
      PAGE_CODES.MARGIN_CRITERIA,
    ),
    redirectRoute(
      LEGACY_PATHS.guardrails,
      MarginCenterPaths.policyTab.generatePath({ tab: POLICY_TAB.GUARDRAILS }),
      PAGE_CODES.MARGIN_GUARDRAIL,
    ),
    redirectRoute(
      LEGACY_PATHS.guardrailsTab,
      MarginCenterPaths.policyTab.generatePath({ tab: POLICY_TAB.GUARDRAILS }),
      PAGE_CODES.MARGIN_GUARDRAIL,
    ),
    redirectRoute(
      LEGACY_PATHS.preview,
      MarginCenterPaths.approvals.path,
      PAGE_CODES.MARGIN_CHANGE_REQUEST,
    ),
    redirectRoute(
      LEGACY_PATHS.concessions,
      MarginCenterPaths.approvals.path,
      PAGE_CODES.MARGIN_CHANGE_REQUEST,
    ),
    redirectRoute(
      LEGACY_PATHS.settings,
      MarginCenterPaths.sources.path,
      PAGE_CODES.MARGIN_GUARDRAIL,
    ),
    {
      path: LEGACY_PATHS.rateSheetSource,
      component: ({ match }) => (
        <Redirect to={MarginCenterPaths.rateSheetSource.generatePath(match.params)} />
      ),
      pageCode: PAGE_CODES.MARGIN_GUARDRAIL,
      exact: true,
    },
    redirectRoute(
      LEGACY_PATHS.programs,
      MarginCenterPaths.productsTab.generatePath({ tab: PRODUCTS_TAB.PROGRAMS }),
      PRODUCTS_CLAIM,
    ),
    redirectRoute(
      LEGACY_PATHS.programsMapper,
      MarginCenterPaths.productsTab.generatePath({ tab: PRODUCTS_TAB.CHANNEL_MAPPING }),
      PRODUCTS_CLAIM,
    ),
    redirectRoute(
      LEGACY_PATHS.programIndexTypes,
      MarginCenterPaths.productsTab.generatePath({ tab: PRODUCTS_TAB.INDEX_TYPES }),
      PRODUCTS_CLAIM,
    ),
    redirectRoute(
      LEGACY_PATHS.channels,
      MarginCenterPaths.productsTab.generatePath({ tab: PRODUCTS_TAB.CHANNELS }),
      PRODUCTS_CLAIM,
    ),
    redirectRoute(
      LEGACY_PATHS.dashboards,
      MarginCenterPaths.reportsTab.generatePath({ tab: REPORTS_TAB.MARGIN }),
    ),
    redirectRoute(
      LEGACY_PATHS.surveillance,
      MarginCenterPaths.reportsTab.generatePath({ tab: REPORTS_TAB.FAIR_LENDING }),
    ),
    redirectRoute(
      LEGACY_PATHS.audit,
      MarginCenterPaths.reportsTab.generatePath({ tab: REPORTS_TAB.QUOTE_REPLAY }),
    ),
  ],
};

export default routesConfig;
